import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { useAuth } from '../../context/AuthContext';
import { API_BASE, getImageUrl } from '../../utils/api';

interface CityItem {
    _id: string;
    name: string;
    isActive: boolean;
}

interface DeveloperUser {
    _id: string;
    name: string;
    email: string;
    avatar: string;
    phone: string;
    projects: string;
    expertise: string;
    city: string;
    about: string;
    totalProjects: string;
    projectsCompleted: string;
    projectsOngoing: string;
    projectsUpcoming: string;
    projectsSince: string;
    deliveryRecord: string;
    isActive: boolean;
    isFeatured: boolean;
}

export default function AdminEditDeveloper() {
    const { token } = useAuth();
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    
    const [cities, setCities] = useState<CityItem[]>([]);
    const [formData, setFormData] = useState({
        name: '', email: '', password: '', phone: '', projects: '', expertise: '', avatar: '', city: '',
        description: '', totalProjects: '0', projectsCompleted: '0', projectsOngoing: '0',
        projectsUpcoming: '0', projectsSince: '', isActive: true, isFeatured: false
    });
    
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch cities
                const citiesRes = await fetch(`${API_BASE}/cities`);
                const citiesData = await citiesRes.json();
                if (Array.isArray(citiesData)) {
                    setCities(citiesData.filter((c: CityItem) => c.isActive));
                }

                // Fetch developer details
                const devRes = await fetch(`${API_BASE}/developers`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const devData = await devRes.json();
                
                if (Array.isArray(devData)) {
                    const dev = devData.find((d: DeveloperUser) => d._id === id);
                    if (dev) {
                        setFormData({
                            name: dev.name || '',
                            email: dev.email || '',
                            password: '', // Hidden on edit by default
                            phone: dev.phone || '',
                            projects: dev.projects || '',
                            expertise: dev.expertise || '',
                            avatar: dev.avatar || '',
                            city: dev.city || '',
                            description: dev.about || '',
                            totalProjects: dev.totalProjects || '0',
                            projectsCompleted: dev.projectsCompleted || '0',
                            projectsOngoing: dev.projectsOngoing || '0',
                            projectsUpcoming: dev.projectsUpcoming || '0',
                            projectsSince: dev.projectsSince || '',
                            isActive: dev.isActive,
                            isFeatured: dev.isFeatured || false
                        });
                    } else {
                        setError('Developer not found');
                        setTimeout(() => navigate('/admin-developers'), 2000);
                    }
                }
            } catch (err) {
                setError('Failed to fetch data');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [id, token, navigate]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value });
    };

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const uploadFormData = new FormData();
        uploadFormData.append('images', file);
        setUploading(true);
        setError('');
        try {
            const res = await fetch(`${API_BASE}/upload`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: uploadFormData
            });
            const data = await res.json();
            if (res.ok && data.urls && data.urls.length > 0) {
                setFormData({ ...formData, avatar: data.urls[0] });
            } else {
                setError(data.message || 'Upload failed');
            }
        } catch (err) {
            setError('Upload failed');
        } finally {
            setUploading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);
        setError('');

        try {
            const res = await fetch(`${API_BASE}/developers/${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(formData)
            });
            const data = await res.json();
            if (res.ok) {
                navigate('/admin-developers');
            } else {
                setError(data.message || 'Operation failed');
            }
        } catch (err) {
            setError('Something went wrong');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <AdminLayout>
                <div className="flex items-center justify-center h-[50vh]">
                    <div className="w-10 h-10 border-4 border-[#05144b] border-t-transparent flex items-center justify-center rounded-full animate-spin"></div>
                </div>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                <div className="flex items-center gap-4">
                    <button 
                        onClick={() => navigate('/admin-developers')}
                        className="w-10 h-10 rounded-md bg-white dark:bg-slate-900 flex items-center justify-center text-blue-950 dark:text-blue-950 hover:text-blue-600 dark:hover:text-blue-400 transition-all border border-blue-900/10 dark:border-slate-800 shadow-sm"
                    >
                        <i className="fa-solid fa-arrow-left"></i>
                    </button>
                    <div>
                        <h1 className="text-3xl font-bold text-blue-950 dark:text-white mb-2">Edit Developer</h1>
                        <p className="text-slate-600 dark:text-slate-400 text-sm">Modify existing developer details.</p>
                    </div>
                </div>
            </div>

            <div className="bg-white dark:bg-slate-900 rounded-md border border-blue-900/10 dark:border-slate-800 shadow-xl shadow-slate-200/50 dark:shadow-none p-6 md:p-8 max-w-4xl">
                <form onSubmit={handleSubmit} className="space-y-8">
                    {error && (
                        <div className="p-4 rounded-xl bg-rose-50 dark:bg-rose-900/10 text-rose-700 dark:text-rose-400 border border-rose-100 dark:border-rose-900/20 flex items-center gap-3">
                            <i className="fa-solid fa-triangle-exclamation"></i>
                            <p className="text-sm font-medium">{error}</p>
                        </div>
                    )}

                    {/* Group 1: Business Identity */}
                    <div className="space-y-4">
                        <div className="flex items-center gap-3 border-l-4 border-blue-950 pl-3">
                            <div className="w-9 h-9 rounded-md bg-blue-50 dark:bg-blue-900/20 text-blue-950 dark:text-blue-400 flex items-center justify-center text-base font-bold shadow-sm border border-blue-100 dark:border-blue-800/30">1</div>
                            <h4 className="text-base font-semibold text-blue-950 dark:text-white">Business identity</h4>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Corporate brand name</label>
                                <div className="relative group">
                                    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-blue-900 group-focus-within:text-blue-600 transition-colors">
                                        <i className="fa-solid fa-building"></i>
                                    </div>
                                    <input
                                        type="text"
                                        name="name"
                                        className="w-full pl-11 pr-4 py-3.5 rounded-md bg-white dark:bg-slate-800/50 border border-blue-900/10 dark:border-slate-700 focus:border-blue-500 focus:bg-white dark:focus:bg-slate-800 dark:text-white transition-all text-base text-blue-950"
                                        placeholder="Enter corporate name"
                                        required
                                        value={formData.name}
                                        onChange={handleChange}
                                    />
                                </div>
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Corporate Logo</label>
                                <div className="flex items-center gap-4 p-2 rounded-xl bg-white dark:bg-slate-800/50 border border-dashed border-blue-950/20 dark:border-slate-700 h-[56px]">
                                    <div className="relative w-10 h-10 shrink-0 shadow-sm rounded-lg overflow-hidden bg-white dark:bg-slate-950 flex items-center justify-center">
                                        {formData.avatar ? (
                                            <img src={getImageUrl(formData.avatar)} className="w-full h-full object-cover" alt="Preview" />
                                        ) : (
                                            <i className="fa-solid fa-cloud-arrow-up text-blue-100"></i>
                                        )}
                                        {uploading && (
                                            <div className="absolute inset-0 bg-white/70 dark:bg-slate-950/70 flex items-center justify-center backdrop-blur-sm">
                                                <div className="w-4 h-4 border-2 border-[#05144b] border-t-transparent rounded-full animate-spin"></div>
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex-1">
                                        <div className="relative overflow-hidden inline-block group/btn">
                                            <button 
                                                type="button"
                                                className="bg-white border border-[#05144b] text-[#05144b] px-4 py-2 rounded-lg text-sm font-semibold transition-all hover:bg-blue-50 active:scale-95 shadow-sm"
                                                disabled={uploading}
                                            >
                                                {uploading ? 'Uploading...' : 'Upload Logo'}
                                            </button>
                                            <input
                                                type="file"
                                                className="absolute inset-0 opacity-0 cursor-pointer"
                                                accept="image/*"
                                                onChange={handleFileUpload}
                                                disabled={uploading}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <hr className="border-blue-900/10 dark:border-slate-800" />

                    {/* Group 2: Access & Locale */}
                    <div className="space-y-4">
                        <div className="flex items-center gap-3 border-l-4 border-blue-950 pl-3">
                            <div className="w-9 h-9 rounded-md bg-blue-50 dark:bg-blue-900/20 text-blue-950 dark:text-blue-400 flex items-center justify-center text-base font-bold shadow-sm border border-blue-100 dark:border-blue-800/30">2</div>
                            <h4 className="text-base font-semibold text-blue-950 dark:text-white">Access & locale</h4>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Email Address</label>
                                <div className="relative group">
                                    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-blue-900 group-focus-within:text-emerald-600 transition-colors">
                                        <i className="fa-solid fa-at"></i>
                                    </div>
                                    <input
                                        type="email"
                                        name="email"
                                        className="w-full pl-11 pr-4 py-3.5 rounded-md bg-slate-50 dark:bg-slate-800/30 border border-blue-900/10 dark:border-slate-700 focus:border-emerald-500 focus:bg-white dark:focus:bg-slate-800 dark:text-white transition-all text-base text-blue-950 opacity-70 cursor-not-allowed"
                                        placeholder="Email address"
                                        required
                                        value={formData.email}
                                        onChange={handleChange}
                                        disabled
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Account Password</label>
                                <div className="relative group">
                                    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-blue-900 group-focus-within:text-emerald-600 transition-colors">
                                        <i className="fa-solid fa-key"></i>
                                    </div>
                                    <input
                                        type="password"
                                        name="password"
                                        className="w-full pl-11 pr-4 py-3.5 rounded-md bg-white dark:bg-slate-800/50 border border-blue-900/10 dark:border-slate-700 focus:border-emerald-500 focus:bg-white dark:focus:bg-slate-800 dark:text-white transition-all text-base text-blue-950"
                                        placeholder="•••••••• (Leave blank to keep current)"
                                        value={formData.password}
                                        onChange={handleChange}
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Contact phone</label>
                                <div className="relative group">
                                    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-blue-900 group-focus-within:text-emerald-600 transition-colors">
                                        <i className="fa-solid fa-phone"></i>
                                    </div>
                                    <input
                                        type="text"
                                        name="phone"
                                        className="w-full pl-11 pr-4 py-3.5 rounded-md bg-white dark:bg-slate-800/50 border border-blue-900/10 dark:border-slate-700 focus:border-emerald-500 focus:bg-white dark:focus:bg-slate-800 dark:text-white transition-all text-base text-blue-950"
                                        placeholder="Phone number"
                                        value={formData.phone}
                                        onChange={handleChange}
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Primary City</label>
                                <div className="relative group">
                                    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-blue-900 group-focus-within:text-emerald-600 transition-colors drop-shadow-sm z-10 pointer-events-none">
                                        <i className="fa-solid fa-map-pin"></i>
                                    </div>
                                    <select
                                        name="city"
                                        className="w-full pl-11 pr-10 py-3.5 rounded-md bg-white dark:bg-slate-800/50 border border-blue-900/10 dark:border-slate-700 focus:border-emerald-500 focus:bg-white dark:focus:bg-slate-800 dark:text-white transition-all text-base appearance-none cursor-pointer text-blue-950"
                                        value={formData.city}
                                        onChange={handleChange}
                                        required
                                    >
                                        <option value="">Select City</option>
                                        {cities.map(c => (
                                            <option key={c._id} value={c.name}>{c.name}</option>
                                        ))}
                                    </select>
                                    <div className="absolute right-4 top-1/2 -translate-y-1/2 text-blue-900 pointer-events-none">
                                        <i className="fa-solid fa-chevron-down text-xs"></i>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <hr className="border-blue-900/10 dark:border-slate-800" />

                    {/* Group 3: Market Presence */}
                    <div className="space-y-4">
                        <div className="flex items-center gap-3 border-l-4 border-blue-950 pl-3">
                            <div className="w-9 h-9 rounded-md bg-indigo-50 dark:bg-indigo-900/20 text-blue-950 dark:text-indigo-400 flex items-center justify-center text-base font-bold shadow-sm border border-indigo-100 dark:border-indigo-800/30">3</div>
                            <h4 className="text-base font-semibold text-blue-950 dark:text-white">Market presence</h4>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
                            <div className="md:col-span-4">
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Expertise</label>
                                <div className="relative group">
                                    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-blue-900 group-focus-within:text-indigo-600 transition-colors">
                                        <i className="fa-solid fa-star"></i>
                                    </div>
                                    <input
                                        type="text"
                                        name="expertise"
                                        className="w-full pl-11 pr-4 py-3 rounded-md bg-white dark:bg-slate-800/50 border border-blue-900/10 dark:border-slate-700 focus:border-indigo-500 focus:bg-white dark:focus:bg-slate-800 dark:text-white transition-all text-base text-blue-950"
                                        placeholder="e.g. Luxury Residential, Commercial"
                                        value={formData.expertise}
                                        onChange={handleChange}
                                    />
                                </div>
                            </div>

                            <div className="md:col-span-4">
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Corporate heritage / About</label>
                                <div className="relative group">
                                    <div className="absolute left-4 top-4 text-blue-900 group-focus-within:text-indigo-600 transition-colors">
                                        <i className="fa-solid fa-quote-left"></i>
                                    </div>
                                    <textarea
                                        name="description"
                                        className="w-full pl-11 pr-4 py-3 rounded-md bg-white dark:bg-slate-800/50 border border-blue-900/10 dark:border-slate-700 focus:border-indigo-500 focus:bg-white dark:focus:bg-slate-800 dark:text-white transition-all text-base min-h-[100px] text-blue-950"
                                        placeholder="Provide a compelling description of the developer..."
                                        rows={3}
                                        value={formData.description}
                                        onChange={handleChange}
                                    />
                                </div>
                            </div>

                             <div className="md:col-span-1 border border-blue-900/10 dark:border-slate-800 rounded-md bg-white/50 dark:bg-slate-800/20 p-4">
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">Since (year)</label>
                                <input
                                    type="text"
                                    name="projectsSince"
                                    className="w-full px-4 py-3 rounded-md bg-white dark:bg-slate-900 border border-blue-900/10 dark:border-slate-700 focus:border-indigo-500 text-center text-base text-blue-950 shadow-sm transition-all"
                                    placeholder="e.g. 1995"
                                    value={formData.projectsSince}
                                    onChange={handleChange}
                                />
                            </div>

                            <div className="md:col-span-1 border border-blue-900/10 dark:border-slate-800 rounded-md bg-white/50 dark:bg-slate-800/20 p-4">
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">Completed</label>
                                <input
                                    type="number"
                                    name="projectsCompleted"
                                    className="w-full px-4 py-3 rounded-md bg-white dark:bg-slate-900 border border-blue-900/10 dark:border-slate-700 focus:border-indigo-500 text-center text-base text-emerald-600 dark:text-emerald-400 shadow-sm transition-all"
                                    placeholder="Count"
                                    value={formData.projectsCompleted}
                                    onChange={handleChange}
                                />
                            </div>

                            <div className="md:col-span-1 border border-blue-900/10 dark:border-slate-800 rounded-md bg-white/50 dark:bg-slate-800/20 p-4">
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">Ongoing</label>
                                <input
                                    type="number"
                                    name="projectsOngoing"
                                    className="w-full px-4 py-3 rounded-md bg-white dark:bg-slate-900 border border-blue-900/10 dark:border-slate-700 focus:border-indigo-500 text-center text-base text-blue-600 dark:text-blue-400 shadow-sm transition-all"
                                    placeholder="Count"
                                    value={formData.projectsOngoing}
                                    onChange={handleChange}
                                />
                            </div>

                            <div className="md:col-span-1 border border-blue-900/10 dark:border-slate-800 rounded-md bg-white/50 dark:bg-slate-800/20 p-4">
                                <label className="block text-[13px] font-medium text-slate-700 dark:text-slate-300 mb-3 flex items-center justify-between">
                                    <span>Upcoming</span>
                                    <div 
                                        className={`relative inline-flex h-4 w-8 shrink-0 cursor-pointer rounded-full border border-transparent transition-all duration-300 ease-in-out ${formData.isActive ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-600'}`}
                                        onClick={() => setFormData(prev => ({ ...prev, isActive: !prev.isActive }))}
                                        title="Toggle Active Status"
                                    >
                                        <span className={`pointer-events-none inline-block h-3 w-3 transform rounded-full bg-white shadow-sm transition duration-300 ease-in-out ${formData.isActive ? 'translate-x-[15px] translate-y-[1px]' : 'translate-x-[1px] translate-y-[1px]'}`} />
                                    </div>
                                </label>
                                <input
                                    type="number"
                                    name="projectsUpcoming"
                                    className="w-full px-4 py-3 rounded-md bg-white dark:bg-slate-900 border border-blue-900/10 dark:border-slate-700 focus:border-indigo-500 text-center text-base text-amber-600 dark:text-amber-500 shadow-sm transition-all"
                                    placeholder="Count"
                                    value={formData.projectsUpcoming}
                                    onChange={handleChange}
                                />
                            <div className="md:col-span-1 border border-blue-900/10 dark:border-slate-800 rounded-md bg-white/50 dark:bg-slate-800/20 p-4 text-center">
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">Featured Developer</label>
                                <div className="flex justify-center">
                                    <button
                                        type="button"
                                        onClick={() => setFormData(prev => ({ ...prev, isFeatured: !prev.isFeatured }))}
                                        className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${formData.isFeatured ? 'bg-amber-100 text-amber-500 shadow-lg' : 'bg-gray-100 text-gray-400 hover:text-amber-400'}`}
                                        title="Feature this developer on home page"
                                    >
                                        <i className={`fa-solid fa-star ${formData.isFeatured ? 'scale-125 text-xl' : 'scale-100'}`}></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                        </div>
                    </div>


                    <div className="pt-6 border-t border-blue-900/10 dark:border-slate-800 flex gap-4">
                        <button 
                            type="button" 
                            className="px-8 py-3.5 bg-blue-50 dark:bg-slate-800 text-blue-950 rounded-md font-semibold text-sm hover:bg-blue-100 dark:hover:bg-slate-700 transition-all active:scale-95 border border-blue-900/10" 
                            onClick={() => navigate('/admin-developers')}
                        >
                            Cancel
                        </button>
                        <button 
                            type="submit" 
                            className="px-10 py-3.5 bg-white border-2 border-[#05144b] text-[#05144b] hover:bg-blue-50 rounded-md font-semibold text-sm shadow-xl transition-all active:scale-95 flex items-center justify-center gap-2 group" 
                            disabled={submitting || uploading}
                        >
                            {submitting ? (
                                <div className="w-4 h-4 border-2 border-[#05144b] border-t-transparent rounded-full animate-spin"></div>
                            ) : (
                                <>
                                    <i className="fa-solid fa-check"></i>
                                    <span>Save Changes</span>
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </AdminLayout>
    );
}


